import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Building2, Truck, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportadoraLogo = ({ importadoraId }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!importadoraId) { setError(true); return; }
    setLoading(true);
    axios.get(`${API}/importadoras/${importadoraId}/logo`)
      .then(res => { setImgSrc(res.data.base64); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [importadoraId]);

  if (error || (!loading && !imgSrc)) {
    return (
      <div className="w-[140px] h-[60px] bg-slate-100 flex items-center justify-center rounded">
        <Building2 size={24} className="text-slate-400" />
      </div>
    );
  }
  if (loading) {
    return (
      <div className="w-[140px] h-[60px] bg-slate-100 flex items-center justify-center rounded">
        <Loader2 size={16} className="text-slate-400 animate-spin" />
      </div>
    );
  }
  return (
    <img src={imgSrc} alt="Logo" className="w-[140px] h-[60px] object-contain rounded border border-slate-200 bg-white p-1" />
  );
};

const LiquidacionViewModal = ({ liquidacion, onClose, onDownload }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatUSD = (value) => {
    if (value === null || value === undefined) return '-';
    return `USD $${value.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            <h2 className="text-sm font-bold text-slate-800" data-testid="liquidacion-view-title">
              Liquidación: {liquidacion.dispatch_number} - {liquidacion.company_name}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onDownload}
              className="h-7 text-xs"
              data-testid="download-pdf-btn"
            >
              <Download size={14} className="mr-1" /> Descargar PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-7 w-7 p-0"
            >
              <X size={16} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Info Cards */}
          <div className="grid grid-cols-2 gap-4">
            {/* Importadora */}
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                <Building2 size={12} /> IMPORTADORA
              </h3>
              <div className="flex gap-3">
                <ImportadoraLogo importadoraId={liquidacion.importadora_id} />
                <div className="space-y-1 text-xs flex-1">
                  <p className="font-semibold text-slate-800">{liquidacion.importadora_razon_social}</p>
                  <p className="text-slate-600">RUT: {liquidacion.importadora_rut}</p>
                  <p className="text-slate-600">{liquidacion.importadora_direccion}, {liquidacion.importadora_comuna}</p>
                  <p className="text-slate-600">{liquidacion.importadora_giro}</p>
                </div>
              </div>
            </div>

            {/* Transportista */}
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                <Truck size={12} /> TRANSPORTISTA
              </h3>
              <div className="space-y-1 text-xs">
                <p className="font-semibold text-slate-800">{liquidacion.company_name}</p>
                <p className="text-slate-600">Despacho: {liquidacion.dispatch_number}</p>
                <p className="text-slate-600">Producto: {liquidacion.producto}</p>
                <p className="text-slate-600">Cliente: {liquidacion.trayecto}</p>
              </div>
            </div>
          </div>

          {/* Tabla de Camiones */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">F. CARGA</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">ORIGEN</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">F. RECEP.</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">DESTINO</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">N° GUÍA</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-slate-600">PATENTE</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">TON DESP.</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">TON RECEP.</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">DIF.</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">DSCTO</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">USD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {liquidacion.trucks?.map((truck, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-2 py-1.5 font-mono">{formatDate(truck.load_date)}</td>
                    <td className="px-2 py-1.5">{truck.origin || '-'}</td>
                    <td className="px-2 py-1.5 font-mono">{formatDate(truck.delivery_date)}</td>
                    <td className="px-2 py-1.5">{truck.destination || '-'}</td>
                    <td className="px-2 py-1.5 font-mono">{truck.guide_number || '-'}</td>
                    <td className="px-2 py-1.5 font-mono font-semibold">{truck.license_plate}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{truck.ton_dispatched?.toFixed(3)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{truck.ton_received?.toFixed(3)}</td>
                    <td className={`px-2 py-1.5 text-right font-mono ${truck.difference > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {truck.difference?.toFixed(3)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-red-600">
                      {truck.discount > 0 ? truck.discount.toFixed(3) : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-600 font-semibold">
                      {truck.price_usd?.toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 font-semibold">
                <tr>
                  <td colSpan={6} className="px-2 py-1.5 text-right">TOTALES:</td>
                  <td className="px-2 py-1.5 text-right font-mono">{liquidacion.total_ton_dispatched?.toFixed(3)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{liquidacion.total_ton_received?.toFixed(3)}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${liquidacion.total_difference > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {liquidacion.total_difference?.toFixed(3)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-red-600">
                    {liquidacion.total_discount > 0 ? liquidacion.total_discount.toFixed(3) : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-600">
                    {liquidacion.total_usd?.toLocaleString('es-CL')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Resumen de Pago y Observaciones */}
          <div className="grid grid-cols-2 gap-4">
            {/* Resumen de Pago */}
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-slate-500 mb-2">RESUMEN DE PAGO</h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total USD:</span>
                  <span className="font-mono font-semibold">{formatUSD(liquidacion.total_usd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Descuentos:</span>
                  <span className="font-mono text-red-600">-{formatUSD(liquidacion.descuentos || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Anticipos:</span>
                  <span className="font-mono text-amber-600">-{formatUSD(liquidacion.anticipos || 0)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-slate-200">
                  <span className="font-semibold">A PAGAR:</span>
                  <span className="font-mono font-bold text-blue-600">{formatUSD(liquidacion.a_pagar)}</span>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-slate-500 mb-2">OBSERVACIONES</h3>
              <p className="text-xs text-slate-700 whitespace-pre-wrap">
                {liquidacion.observacion || 'Sin observaciones'}
              </p>
              {liquidacion.numero_factura && (
                <p className="text-xs text-slate-600 mt-2">
                  <span className="font-semibold">N° Factura:</span> {liquidacion.numero_factura}
                </p>
              )}
              {liquidacion.numero_cheque && (
                <p className="text-xs text-slate-600">
                  <span className="font-semibold">N° Cheque:</span> {liquidacion.numero_cheque}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidacionViewModal;
